/** Shared timesheet constants (UI + Backend) */

export const STATUSES = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REFUSED: 'refused',
};

export const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  refused: 'Declined',
};

export const STATUS_COLORS = {
  draft: '#757682',
  pending: '#1e3a8a',
  approved: '#2e7d32',
  refused: '#ba1a1a',
};

export const ENTRY_CATEGORIES = [
  {
    value: 'standard_hours',
    label: 'Standard Hours',
    jobRelated: false,
    needsTime: false,
  },
  { value: 'overtime', label: 'Overtime', jobRelated: true, needsTime: true },
  { value: 'travel', label: 'Travel', jobRelated: true, needsTime: true },
  {
    value: 'on_call_travel',
    label: 'On-Call Travel',
    jobRelated: true,
    needsTime: true,
  },
  {
    value: 'on_call_onsite',
    label: 'On-Call On-Site',
    jobRelated: true,
    needsTime: true,
  },
  {
    value: 'on_call_allowance',
    label: 'On-Call Allowance',
    jobRelated: false,
    needsTime: false,
  },
  {
    value: 'collecting_kit',
    label: 'Collecting Kit',
    jobRelated: false,
    needsTime: false,
  },
  { value: 'sickness', label: 'Sickness', jobRelated: false, needsTime: true },
  { value: 'holiday', label: 'Holiday', jobRelated: false, needsTime: true },
  {
    value: 'auth_absence',
    label: 'Auth Absence',
    jobRelated: false,
    needsTime: true,
  },
];

/** Categories shown in the Exception dropdown (excludes standard-hours-only). */
export const EXCEPTION_CATEGORIES = ENTRY_CATEGORIES.filter(
  c => c.value !== 'standard_hours',
);

export const STANDARD_HOURS_CATEGORY = 'standard_hours';

export const ABSENCE_CATEGORIES = ['sickness', 'holiday', 'auth_absence'];

export const TIME_CATEGORIES = [
  'overtime',
  'travel',
  'on_call_travel',
  'on_call_onsite',
  'sickness',
  'holiday',
  'auth_absence',
];

/**
 * Persist standard-hours-only days as category `standard_hours`.
 * Existing exception categories are left unchanged (SoW 3.4: WSH may combine
 * with Overtime / Travel / On-Call / Collecting Kit).
 */
export const normalizeTimesheetCategory = entry => {
  const category = entry && entry.category ? String(entry.category).trim() : '';
  if (category && category !== STANDARD_HOURS_CATEGORY) return category;
  if (entry && entry.workedStandardHours) return STANDARD_HOURS_CATEGORY;
  if (category === STANDARD_HOURS_CATEGORY) return STANDARD_HOURS_CATEGORY;
  return '';
};
export const MILEAGE_RATE = 0.14;
export const STANDARD_HOURS = 8;
export const TRAVEL_DEDUCTION_MINUTES = 30;
export const KIT_AUTO_HOURS = 1;
export const DATA_RETENTION_DAYS = 60;
/**
 * Missing-day scan window (calendar days before today).
 * Completely missing days older than this are not auto-suggested.
 */
export const LOOKBACK_DAYS = 30;
/**
 * Site-wide go-live / first day timesheets apply (YYYY-MM-DD).
 * Missing days before this date are not flagged for any engineer.
 */
export const TIMESHEET_START_DATE = '2026-10-26';
/** Option C action-token lifetime (hours). */
export const ACTION_TOKEN_TTL_HOURS = 72;

/**
 * Payroll / org department tabs (display order).
 * Add new customer department names here when confirmed.
 */
export const DEPARTMENTS = [
  'Install',
  'Service & Maintenance',
  'Electrical',
  'Aberdeen',
];

/** Map legacy provision values to current department names. */
const LEGACY_DEPARTMENT_ALIASES = {
  INSTAL: 'Install',
  INSTALL: 'Install',
  REACTIVE: 'Service & Maintenance',
};

/** Trim profile/CSV department; map legacy INSTAL/REACTIVE when present. */
export const normalizeDepartment = value => {
  const trimmed = String(value == null ? '' : value).trim();
  if (!trimmed) return '';
  const alias = LEGACY_DEPARTMENT_ALIASES[trimmed.toUpperCase()];
  return alias || trimmed;
};

/**
 * Export tab order: configured DEPARTMENTS first, then any extra keys in data (A–Z).
 */
export const resolveDepartmentTabs = (dataDepartments = {}) => {
  const keys = Object.keys(dataDepartments || {}).filter(Boolean);
  const extras = keys
    .filter(name => !DEPARTMENTS.includes(name))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [...DEPARTMENTS, ...extras];
};

export const isKnownDepartment = value =>
  DEPARTMENTS.includes(normalizeDepartment(value));

export const ROLES = {
  ENGINEER: 'engineer',
  FIELD_MANAGER: 'field_manager',
  SYSTEM_ADMIN: 'system_admin',
};

export const MULTIPLIER_LABELS = {
  1: 'Normal',
  1.5: 'OT 1.5×',
  2: 'DT 2.0×',
};
